var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/platform.ts
import { G } from "@mobily/ts-belt";
import { match } from "ts-pattern";
import { getEnv, getEnvironment } from "./core.js";
function detectRuntime() {
  if (isBrowser()) {
    return "browser";
  }
  if (isNode()) {
    return "node";
  }
  if (isEdge()) {
    return "edge";
  }
  return "unknown";
}
__name(detectRuntime, "detectRuntime");
function getRuntimeInfo() {
  const runtime = detectRuntime();
  const isClient = isBrowserRuntime();
  const isServer = isNodeRuntime() || isEdgeRuntime();
  return {
    isClient,
    isServer,
    platform: runtime,
    supportsFileSystem: isNodeRuntime(),
    supportsWebAPIs: isBrowserRuntime() || isEdgeRuntime()
  };
}
__name(getRuntimeInfo, "getRuntimeInfo");
function isBrowserRuntime() {
  return detectRuntime() === "browser";
}
__name(isBrowserRuntime, "isBrowserRuntime");
function isNodeRuntime() {
  return detectRuntime() === "node";
}
__name(isNodeRuntime, "isNodeRuntime");
function isEdgeRuntime() {
  return detectRuntime() === "edge";
}
__name(isEdgeRuntime, "isEdgeRuntime");
function detectPlatform() {
  if (isBrowser()) {
    return getEnv("NEXT_PUBLIC_VERCEL_ENV") != null ? "vercel" : "local";
  }
  if (hasVercelEnv()) {
    return "vercel";
  }
  if (hasAWSEnv()) {
    return "aws";
  }
  if (hasCloudflareEnv()) {
    return "cloudflare";
  }
  if (hasRailwayEnv()) {
    return "railway";
  }
  if (hasRenderEnv()) {
    return "render";
  }
  if (isLocalDevelopment()) {
    return "local";
  }
  return "unknown";
}
__name(detectPlatform, "detectPlatform");
function isVercel() {
  return detectPlatform() === "vercel";
}
__name(isVercel, "isVercel");
function isVercelProductionDeployment() {
  return isVercel() && getEnv("VERCEL_ENV") === "production";
}
__name(isVercelProductionDeployment, "isVercelProductionDeployment");
function isLocal() {
  return detectPlatform() === "local";
}
__name(isLocal, "isLocal");
function isAWS() {
  return detectPlatform() === "aws";
}
__name(isAWS, "isAWS");
function isCloudflare() {
  return detectPlatform() === "cloudflare";
}
__name(isCloudflare, "isCloudflare");
function isRailway() {
  return detectPlatform() === "railway";
}
__name(isRailway, "isRailway");
function isRender() {
  return detectPlatform() === "render";
}
__name(isRender, "isRender");
function resolveSelfInvokeBaseUrl(fallbackPort = "3000") {
  const override = getEnv("SELF_INVOKE_BASE_URL");
  if (override) {
    return stripTrailingSlash(override);
  }
  const appUrl = getEnv("NEXT_PUBLIC_APP_URL");
  if (appUrl) {
    return stripTrailingSlash(appUrl);
  }
  if (getEnv("VERCEL_ENV") === "production") {
    const productionHost = getEnv("VERCEL_PROJECT_PRODUCTION_URL");
    if (productionHost) {
      return `https://${stripTrailingSlash(productionHost)}`;
    }
  }
  const deploymentHost = getEnv("VERCEL_URL");
  if (deploymentHost) {
    return `https://${stripTrailingSlash(deploymentHost)}`;
  }
  return `http://localhost:${fallbackPort}`;
}
__name(resolveSelfInvokeBaseUrl, "resolveSelfInvokeBaseUrl");
function stripTrailingSlash(value) {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}
__name(stripTrailingSlash, "stripTrailingSlash");
function getBaseUrl() {
  const appUrl = getEnv("NEXT_PUBLIC_APP_URL");
  if (appUrl) {
    return appUrl;
  }
  const environment = getEnvironment();
  const platform = detectPlatform();
  if (platform === "vercel") {
    const vercelUrl = getEnv("VERCEL_URL");
    if (vercelUrl) {
      return `https://${vercelUrl}`;
    }
  }
  if (platform === "railway") {
    const railwayUrl = getEnv("RAILWAY_STATIC_URL");
    if (railwayUrl) {
      return railwayUrl;
    }
  }
  if (platform === "render") {
    const renderUrl = getEnv("RENDER_EXTERNAL_URL");
    if (renderUrl) {
      return renderUrl;
    }
  }
  return match(environment).with("production", () => "https://").with("staging",
  () => "https://staging.pulseventures.co.uk").otherwise(() => "http://localhost\
:3000");
}
__name(getBaseUrl, "getBaseUrl");
function getApiUrl() {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/api`;
}
__name(getApiUrl, "getApiUrl");
function getTrpcUrl() {
  if (typeof window !== "undefined") {
    return "/api/trpc";
  }
  const platform = detectPlatform();
  if (platform === "vercel") {
    const vercelUrl = getEnv("VERCEL_URL");
    if (vercelUrl) {
      return `https://${vercelUrl}/api/trpc`;
    }
  }
  return `${getApiUrl()}/trpc`;
}
__name(getTrpcUrl, "getTrpcUrl");
function getWebSocketUrl() {
  const baseUrl = getBaseUrl();
  const wsProtocol = baseUrl.startsWith("https://") ? "wss://" : "ws://";
  return baseUrl.replace(/^https?:\/\//, wsProtocol);
}
__name(getWebSocketUrl, "getWebSocketUrl");
function getStorageUrl() {
  const platform = detectPlatform();
  if (platform === "vercel") {
    return getEnv("VERCEL_BLOB_STORE_URL", { defaultValue: getBaseUrl() });
  }
  if (platform === "aws") {
    const s3Bucket = getEnv("AWS_S3_BUCKET_NAME");
    if (s3Bucket) {
      const region = getEnv("AWS_REGION", { defaultValue: "us-east-1" });
      return `https://${s3Bucket}.s3.${region}.amazonaws.com`;
    }
  }
  return `${getBaseUrl()}/storage`;
}
__name(getStorageUrl, "getStorageUrl");
function getPlatformConfig() {
  const platform = detectPlatform();
  return {
    platform,
    baseUrl: getBaseUrl(),
    apiUrl: getApiUrl(),
    trpcUrl: getTrpcUrl(),
    webSocketUrl: getWebSocketUrl(),
    storageUrl: getStorageUrl(),
    supportsWebSockets: platform !== "cloudflare",
    // Cloudflare Workers have limited WebSocket support
    supportsFileSystem: platform === "local" || platform === "aws",
    supportsDatabase: true
    // Most platforms support database connections
  };
}
__name(getPlatformConfig, "getPlatformConfig");
function isBrowser() {
  return typeof window !== "undefined" && typeof window.document !== "undefined";
}
__name(isBrowser, "isBrowser");
function isNode() {
  if ("EdgeRuntime" in globalThis) {
    return false;
  }
  return typeof process !== "undefined" && typeof globalThis.process !== "undefi\
ned" && G.isNotNullable(globalThis.process?.versions?.node);
}
__name(isNode, "isNode");
function isEdge() {
  return "EdgeRuntime" in globalThis;
}
__name(isEdge, "isEdge");
function hasVercelEnv() {
  if (isBrowser()) {
    return !!getEnv("NEXT_PUBLIC_VERCEL_ENV");
  }
  return getEnv("VERCEL") === "1" || !!getEnv("VERCEL_URL") || !!getEnv("VERCEL_\
GIT_COMMIT_SHA");
}
__name(hasVercelEnv, "hasVercelEnv");
function hasAWSEnv() {
  if (isBrowser()) {
    return false;
  }
  return !!getEnv("AWS_LAMBDA_FUNCTION_NAME") || !!getEnv("AWS_EXECUTION_ENV") ||
  !!getEnv("AWS_REGION") || !!getEnv("ECS_CONTAINER_METADATA_URI");
}
__name(hasAWSEnv, "hasAWSEnv");
function hasCloudflareEnv() {
  if (isBrowser()) {
    return false;
  }
  return !!getEnv("CF_PAGES") || !!getEnv("CF_PAGES_URL") || typeof globalThis.caches !==
  "undefined";
}
__name(hasCloudflareEnv, "hasCloudflareEnv");
function hasRailwayEnv() {
  if (isBrowser()) {
    return false;
  }
  return !!getEnv("RAILWAY_ENVIRONMENT") || !!getEnv("RAILWAY_PROJECT_ID");
}
__name(hasRailwayEnv, "hasRailwayEnv");
function hasRenderEnv() {
  if (isBrowser()) {
    return false;
  }
  return !!getEnv("RENDER") || !!getEnv("RENDER_SERVICE_ID");
}
__name(hasRenderEnv, "hasRenderEnv");
function isLocalDevelopment() {
  const nodeEnv = getEnv("NODE_ENV", { defaultValue: "development" });
  return nodeEnv === "development" && !hasVercelEnv() && !hasAWSEnv();
}
__name(isLocalDevelopment, "isLocalDevelopment");
export {
  detectPlatform,
  detectRuntime,
  getApiUrl,
  getBaseUrl,
  getPlatformConfig,
  getRuntimeInfo,
  getStorageUrl,
  getTrpcUrl,
  getWebSocketUrl,
  isAWS,
  isBrowserRuntime,
  isCloudflare,
  isEdgeRuntime,
  isLocal,
  isNodeRuntime,
  isRailway,
  isRender,
  isVercel,
  isVercelProductionDeployment,
  resolveSelfInvokeBaseUrl
};
